import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import "./App.css";
import logo from "./assetpal-logo.png";
import heroLogo from "./AssetPal-logo2.png";
import ResetPassword from "./ResetPassword";
import AuthCallback from "./AuthCallback";
import TermsOfService from "./TermsOfService";
import PrivacyPolicy from "./PrivacyPolicy";

import screen01 from "./screenshots/screen-01.png";
import screen02 from "./screenshots/screen-02.png";
import screen03 from "./screenshots/screen-03.png";
import screen04 from "./screenshots/screen-04.png";
import screen05 from "./screenshots/screen-05.png";
import screen06 from "./screenshots/screen-06.png";
import screen07 from "./screenshots/screen-07.png";
import screen08 from "./screenshots/screen-08.png";
import screen09 from "./screenshots/screen-09.png";
import screen10 from "./screenshots/screen-10.png";
import screen11 from "./screenshots/screen-11.png";

/* ─────────────────────────────────────────────
   Screenshot Frame Component
───────────────────────────────────────────── */
function ScreenshotFrame({ src, alt = "App screenshot", style = {} }) {
  return (
    <div className="screenshot-frame" style={style}>
      <div className="screenshot-frame-inner">
        <img src={src} alt={alt} className="screenshot-img" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Animated Counter
───────────────────────────────────────────── */
function AnimatedStat({ value, suffix = "", label }) {
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const duration = 1500;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      setCount(Math.floor(progress * value));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, value]);

  return (
    <div className="stat-item" ref={ref}>
      <span className="stat-number">{count}{suffix}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   FAQ Accordion Item
───────────────────────────────────────────── */
function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item${open ? " faq-item-open" : ""}`}>
      <button className="faq-question" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>{question}</span>
        <span className="faq-chevron">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="faq-answer">{answer}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Feature Icons (SVG, no emoji)
───────────────────────────────────────────── */
const ICONS = {
  home: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />,
  bell: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />,
  cloud: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />,
  shield: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />,
  lock: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />,
  camera: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />,
};

function FeatureIcon({ name }) {
  return (
    <svg className="feature-icon-svg" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      {ICONS[name]}
    </svg>
  );
}

/* ─────────────────────────────────────────────
   Home Page
───────────────────────────────────────────── */
function HomePage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="splash-container">
      <AuthCallback />

      {/* ── Navigation ── */}
      <nav className={`nav-bar${scrolled ? " nav-scrolled" : ""}`}>
        <div className="nav-left">
          <img src={logo} alt="AssetPal Logo" className="nav-logo" />
          <span className="nav-title">AssetPal</span>
        </div>
        <button className="nav-hamburger" aria-label="Toggle menu" onClick={() => setMenuOpen((o) => !o)}>
          <span /><span /><span />
        </button>
        <div className={`nav-links${menuOpen ? " nav-links-open" : ""}`}>
          <a href="#features" className="nav-link" onClick={() => setMenuOpen(false)}>Features</a>
          <a href="#screenshots" className="nav-link" onClick={() => setMenuOpen(false)}>App</a>
          <a href="#faq" className="nav-link" onClick={() => setMenuOpen(false)}>FAQ</a>
          <a href="mailto:support@assetpal.org" className="nav-link" onClick={() => setMenuOpen(false)}>Contact</a>
          <a href="https://apps.apple.com/ng/app/assetpal/id6757312649" target="_blank" rel="noopener noreferrer" className="cta-button nav-cta" onClick={() => setMenuOpen(false)}>
            Download
          </a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className="hero-section">
        <div className="hero-bg-orbs">
          <div className="orb orb-1" />
          <div className="orb orb-2" />
          <div className="orb orb-3" />
        </div>
        <div className="hero-content">
          <div className="hero-logo-row">
            <div className="hero-logo">
              <img src={heroLogo} alt="AssetPal Logo" className="hero-logo-img" />
            </div>
            <div className="hero-badge"><span className="hero-badge-dot" />Available now on iOS</div>
          </div>
          <h1>Know what you own.<br /><span className="hero-gradient-text">Keep it that way.</span></h1>
          <p className="subtitle">
            AssetPal is the simplest way to track your home, vehicles, and valuables—
            warranties, maintenance reminders, and all the details you always forget.
          </p>
          <div className="hero-actions">
            <a href="https://apps.apple.com/ng/app/assetpal/id6757312649" target="_blank" rel="noopener noreferrer" className="cta-button cta-primary">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{marginRight:"0.4rem",verticalAlign:"middle"}}>
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Download on the App Store
            </a>
            <a href="#screenshots" className="cta-button cta-secondary">See the app</a>
          </div>
          <div className="hero-social-proof">
            <span className="hero-proof-text">Free trial included — no commitment required</span>
          </div>
        </div>

        {/* Hero phone showcase */}
        <div className="hero-phones">
          <ScreenshotFrame src={screen01} alt="AssetPal dashboard" style={{ "--delay": "0.15s" }} />
          <ScreenshotFrame src={screen02} alt="AssetPal asset detail" style={{ "--delay": "0.3s" }} />
          <ScreenshotFrame src={screen03} alt="AssetPal reminders" style={{ "--delay": "0.45s" }} />
        </div>
      </header>

      {/* ── Stats Banner ── */}
      <section className="stats-section">
        <AnimatedStat value={10} suffix="+" label="Asset categories" />
        <div className="stats-divider" />
        <AnimatedStat value={100} suffix="%" label="Encrypted storage" />
        <div className="stats-divider" />
        <AnimatedStat value={60} suffix="s" label="To add your first asset" />
      </section>

      {/* ── Features ── */}
      <section className="features" id="features">
        <div className="section-label">Features</div>
        <h2>Built for people who actually own things.</h2>
        <p className="section-subtitle">
          Most people keep warranty cards in a drawer and forget oil changes until the light comes on.
          AssetPal fixes that without making it complicated.
        </p>
        <div className="features-grid">
          {[
            { icon: "home", title: "Everything in one place", desc: "Home, vehicles, electronics, appliances—add anything you own. Each asset gets its own record with the details that matter to you." },
            { icon: "bell", title: "Maintenance reminders that make sense", desc: "AssetPal looks at what you own and figures out when things need attention. Not generic advice—reminders based on your actual stuff." },
            { icon: "cloud", title: "Synced across your devices", desc: "Add an asset on your phone, view it anywhere. Your data is encrypted and backed up automatically." },
            { icon: "shield", title: "Warranty & document storage", desc: "Stop digging through email for receipts. Attach warranties, manuals, and purchase docs to each asset and find them instantly." },
            { icon: "lock", title: "Private by design", desc: "Your data belongs to you. Everything is encrypted at rest and in transit, with row-level access controls so only you can see your records." },
            { icon: "camera", title: "Photo records", desc: "Take photos of serial number plates, condition, or damage. Useful for insurance claims, future sales, or just knowing what you have." },
          ].map((f) => (
            <div className="feature-card" key={f.title}>
              <FeatureIcon name={f.icon} />
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── App Screenshots Showcase ── */}
      <section className="screenshots-section" id="screenshots">
        <div className="section-label">The App</div>
        <h2>Simple to use. Genuinely useful.</h2>
        <p className="section-subtitle">
          No learning curve. Add an asset, fill in what you know, and AssetPal takes it from there.
        </p>

        <div className="screenshots-showcase">
          <div className="screenshot-feature">
            <div className="screenshot-phone-wrap">
              <ScreenshotFrame src={screen04} alt="Asset dashboard" />
            </div>
            <div className="screenshot-text">
              <div className="screenshot-step-badge">Getting started</div>
              <h3>Set up in under a minute</h3>
              <p>Pick a category, fill in the details you have, and you're done. AssetPal doesn't demand perfection—you can always add more later. Most people have their first few assets added in under five minutes.</p>
              <ul className="screenshot-check-list">
                <li>Pre-built templates for common asset types</li>
                <li>Guided setup with helpful prompts</li>
                <li>Reminders generated automatically on first add</li>
              </ul>
            </div>
          </div>

          <div className="screenshot-feature screenshot-feature-reverse">
            <div className="screenshot-text">
              <div className="screenshot-step-badge">Maintenance</div>
              <h3>Reminders that actually apply to you</h3>
              <p>AssetPal looks at what you've added and builds a maintenance schedule around it. Your car gets mileage-based reminders. Your HVAC gets filter change alerts. Nothing is generic—it's all based on what you own.</p>
              <ul className="screenshot-check-list">
                <li>Tailored to each specific asset you add</li>
                <li>Push notifications before things are overdue</li>
                <li>Tracks mileage, age, and service history</li>
              </ul>
            </div>
            <div className="screenshot-phone-wrap">
              <ScreenshotFrame src={screen05} alt="AI reminders" />
            </div>
          </div>

          <div className="screenshot-feature">
            <div className="screenshot-phone-wrap">
              <ScreenshotFrame src={screen06} alt="Asset detail view" />
            </div>
            <div className="screenshot-text">
              <div className="screenshot-step-badge">Asset details</div>
              <h3>The details you need, when you need them</h3>
              <p>Serial numbers, purchase dates, warranty expirations, attached documents—it's all there when your appliance breaks or your car needs service. Stop searching email threads and junk drawers.</p>
              <ul className="screenshot-check-list">
                <li>Warranty expiration dates with alerts</li>
                <li>Attached photos, receipts, and manuals</li>
                <li>Purchase price and estimated current value</li>
              </ul>
            </div>
          </div>
        </div>

        {/* ── Full gallery strip ── */}
        <div className="gallery-strip">
          {[screen07, screen08, screen09, screen10, screen11].map((src, i) => (
            <ScreenshotFrame key={i} src={src} alt={`App screen ${i + 7}`} />
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="faq-section" id="faq">
        <div className="section-label">FAQ</div>
        <h2>Common questions.</h2>
        <div className="faq-list">
          {[
            { q: "Is there a free trial?", a: "Yes. When you download AssetPal, you get a free trial period to explore the full app before your subscription begins. You won't be charged until the trial ends, and you can cancel before then through your Apple ID settings." },
            { q: "How much does it cost?", a: "Subscription pricing is shown inside the app at the time of purchase. We offer both monthly and annual options." },
            { q: "What can I actually track?", a: "Anything you own. Most people start with their home appliances, car, and electronics. You can also track furniture, tools, sporting equipment, jewelry—if you can name it, you can add it." },
            { q: "Is my data private?", a: "Yes. Your data is encrypted in transit and at rest. We use row-level security so no one but you can access your records—not even us for normal operations." },
            { q: "How do the maintenance reminders work?", a: "When you add an asset, AssetPal generates a maintenance schedule based on what it is. A car gets oil change and tire rotation reminders. A water heater gets a flush reminder. It's based on the asset type, not a generic checklist." },
            { q: "Does it work without internet?", a: "You can browse your existing assets offline. New additions and edits sync when you're back online." },
            { q: "How do I cancel?", a: "Through your Apple ID account settings under Subscriptions. You'll keep access until the end of the billing period you've already paid for." },
          ].map(({ q, a }) => <FAQItem key={q} question={q} answer={a} />)}
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="cta-banner" id="contact">
        <div className="cta-banner-orb" />
        <div className="cta-banner-content">
          <h2>Give it a try.</h2>
          <p>Download free and start your trial. No commitment until you're ready.</p>
          <div className="cta-banner-actions">
            <a href="https://apps.apple.com/ng/app/assetpal/id6757312649" target="_blank" rel="noopener noreferrer" className="cta-button cta-primary cta-large">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{marginRight:"0.5rem",verticalAlign:"middle"}}>
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Download on the App Store
            </a>
            <a href="mailto:support@assetpal.org" className="cta-button cta-secondary">Get in touch</a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="site-footer">
        <div className="footer-top">
          <div className="footer-brand">
            <img src={logo} alt="AssetPal Logo" className="footer-logo" />
            <span className="footer-brand-name">AssetPal</span>
            <p className="footer-tagline">Personal asset management for iOS.<br />Built in Tennessee.</p>
          </div>
          <div className="footer-links-col">
            <span className="footer-col-title">Product</span>
            <a href="#features">Features</a>
            <a href="#screenshots">App Preview</a>
            <a href="https://apps.apple.com/ng/app/assetpal/id6757312649" target="_blank" rel="noopener noreferrer">Download</a>
          </div>
          <div className="footer-links-col">
            <span className="footer-col-title">Support</span>
            <a href="#faq">FAQ</a>
            <a href="mailto:support@assetpal.org">Contact Us</a>
          </div>
          <div className="footer-links-col">
            <span className="footer-col-title">Legal</span>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} AssetPal. All rights reserved.</p>
          <div className="footer-legal-links">
            <Link to="/privacy">Privacy</Link>
            <span>·</span>
            <Link to="/terms">Terms</Link>
            <span>·</span>
            <a href="mailto:support@assetpal.org">support@assetpal.org</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
      </Routes>
    </Router>
  );
}

export default App;
